// views/adminEstablissement/ManageContainerCardsPage.js

import React, { useState, useEffect } from 'react';
import { 
    Button, 
    Card, 
    CardContent, 
    Grid, 
    makeStyles, 
    Typography, 
    Modal, 
    TextField,
    MenuItem, 
    Select, 
    FormControl, 
    InputLabel, 
    Checkbox, 
    FormControlLabel,
    Paper,
    Box,
    Chip,
    IconButton,
    Fade,
    Backdrop,
    Container
} from '@material-ui/core';
import { createTheme, ThemeProvider } from '@material-ui/core/styles';
import AddIcon from '@material-ui/icons/Add';
import EditIcon from '@material-ui/icons/Edit';
import DeleteIcon from '@material-ui/icons/Delete';
import CloseIcon from '@material-ui/icons/Close';
import StorageIcon from '@material-ui/icons/Storage';
import EcoIcon from '@material-ui/icons/Eco';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import CancelIcon from '@material-ui/icons/Cancel';
import axios from 'axios';
import Navbar from '../../components/Navbar';
import axiosInstance from '../../services/axiosInstance';

const theme = createTheme({
    palette: {
        primary: {
            main: '#059669',
            light: '#10b981',
            dark: '#047857',
        },
        secondary: {
            main: '#dc2626',
            light: '#ef4444',
            dark: '#b91c1c',
        },
        background: {
            default: '#f8fafc',
            paper: '#ffffff',
        },
    },
    typography: {
        fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
        h4: {
            fontWeight: 700,
            color: '#1e293b',
        },
        h6: {
            fontWeight: 600,
            color: '#334155',
        },
        body1: {
            color: '#64748b',
        },
        body2: {
            color: '#64748b',
        },
    },
    shape: {
        borderRadius: 12,
    },
});

const useStyles = makeStyles((theme) => ({
    root: {
        minHeight: '100vh',
        backgroundColor: theme.palette.background.default,
        paddingBottom: theme.spacing(4),
    },
    header: {
        background: 'linear-gradient(135deg, #059669 0%, #047857 100%)',
        color: 'white',
        padding: theme.spacing(4, 0),
        marginBottom: theme.spacing(4),
        boxShadow: '0 10px 40px rgba(5, 150, 105, 0.2)',
    },
    headerContent: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: theme.spacing(2),
    },
    headerTitle: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(2),
    },
    headerIcon: {
        fontSize: '4rem',
        opacity: 0.3,
    },
    addButtonContainer: {
        display: 'flex',
        justifyContent: 'center',
        marginBottom: theme.spacing(4),
    },
    addButton: {
        background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
        color: 'white',
        padding: theme.spacing(1.5, 4),
        borderRadius: theme.shape.borderRadius,
        fontWeight: 600,
        fontSize: '1.1rem',
        textTransform: 'none',
        boxShadow: '0 8px 25px rgba(5, 150, 105, 0.3)',
        transition: 'all 0.3s ease',
        '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: '0 12px 35px rgba(5, 150, 105, 0.4)',
            background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, #065f46 100%)`,
        },
    },
    card: {
        height: '100%',
        borderRadius: theme.shape.borderRadius,
        transition: 'all 0.3s ease',
        border: '1px solid rgba(226, 232, 240, 0.5)',
        overflow: 'hidden',
        '&:hover': {
            transform: 'translateY(-8px)',
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.15)',
            borderColor: theme.palette.primary.light,
        },
    },
    cardContent: {
        padding: 0,
        '&:last-child': {
            paddingBottom: 0,
        },
    },
    cardImage: {
        width: '100%',
        height: 200,
        objectFit: 'cover',
        borderRadius: `${theme.shape.borderRadius}px ${theme.shape.borderRadius}px 0 0`,
    },
    cardInfo: {
        padding: theme.spacing(2.5),
    },
    cardName: {
        fontSize: '1.25rem',
        fontWeight: 600,
        color: '#1e293b',
        marginBottom: theme.spacing(1),
    },
    cardDetail: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: theme.spacing(0.5),
        padding: theme.spacing(0.5, 0),
    },
    cardLabel: {
        fontWeight: 500,
        color: '#64748b',
        fontSize: '0.9rem',
    },
    cardValue: {
        fontWeight: 600,
        color: '#334155',
        fontSize: '0.9rem',
    },
    typeChip: {
        fontWeight: 600,
        fontSize: '0.8rem',
        backgroundColor: '#dcfce7',
        color: '#16a34a',
    },
    transparentChip: {
        fontWeight: 600,
        fontSize: '0.8rem',
        marginTop: theme.spacing(1),
    },
    transparentTrue: {
        backgroundColor: '#dbeafe',
        color: '#2563eb',
    },
    transparentFalse: {
        backgroundColor: '#fef3c7',
        color: '#d97706',
    },
    actionButtons: {
        display: 'flex',
        gap: theme.spacing(1),
        padding: theme.spacing(0, 2.5, 2.5),
    },
    editButton: {
        flex: 1,
        backgroundColor: theme.palette.primary.main,
        color: 'white',
        padding: theme.spacing(1),
        borderRadius: theme.shape.borderRadius,
        fontWeight: 500,
        fontSize: '0.9rem',
        textTransform: 'none',
        transition: 'all 0.3s ease',
        '&:hover': {
            backgroundColor: theme.palette.primary.dark,
            transform: 'translateY(-1px)',
        },
    },
    deleteButton: {
        flex: 1,
        backgroundColor: theme.palette.secondary.main,
        color: 'white',
        padding: theme.spacing(1),
        borderRadius: theme.shape.borderRadius,
        fontWeight: 500,
        fontSize: '0.9rem',
        textTransform: 'none',
        transition: 'all 0.3s ease',
        '&:hover': {
            backgroundColor: theme.palette.secondary.dark,
            transform: 'translateY(-1px)',
        },
    },
    modal: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: theme.spacing(2),
    },
    modalContent: {
        backgroundColor: 'white',
        borderRadius: theme.shape.borderRadius,
        padding: theme.spacing(4),
        maxWidth: 600,
        width: '100%',
        maxHeight: '80vh',
        overflowY: 'auto',
        boxShadow: '0 25px 80px rgba(0, 0, 0, 0.2)',
        border: 'none',
        outline: 'none',
    },
    modalHeader: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: theme.spacing(3),
    },
    modalTitle: {
        fontSize: '1.5rem',
        fontWeight: 600,
        color: '#1e293b',
    },
    closeButton: {
        color: '#64748b',
        transition: 'all 0.3s ease',
        '&:hover': {
            color: theme.palette.secondary.main,
            backgroundColor: 'rgba(220, 38, 38, 0.1)',
        },
    },
    textField: {
        marginBottom: theme.spacing(2),
        '& .MuiOutlinedInput-root': {
            borderRadius: theme.shape.borderRadius,
            transition: 'all 0.3s ease',
            '&:hover': {
                '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: theme.palette.primary.light,
                },
            },
            '&.Mui-focused': {
                '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: theme.palette.primary.main,
                    borderWidth: 2,
                },
            },
        },
        '& .MuiInputLabel-root': {
            fontWeight: 500,
        },
    },
    modalActions: {
        display: 'flex',
        gap: theme.spacing(2),
        marginTop: theme.spacing(3),
        justifyContent: 'flex-end',
    },
    modalButton: {
        padding: theme.spacing(1.5, 3),
        borderRadius: theme.shape.borderRadius,
        fontWeight: 600,
        textTransform: 'none',
        transition: 'all 0.3s ease',
    },
    cancelButton: {
        backgroundColor: '#f1f5f9',
        color: '#64748b',
        '&:hover': {
            backgroundColor: '#e2e8f0',
            transform: 'translateY(-1px)',
        },
    },
    saveButton: {
        backgroundColor: theme.palette.primary.main,
        color: 'white',
        '&:hover': {
            backgroundColor: theme.palette.primary.dark,
            transform: 'translateY(-1px)',
        },
    },
    deleteModalContent: {
        textAlign: 'center',
        padding: theme.spacing(4),
    },
    deleteIcon: {
        fontSize: '4rem',
        color: theme.palette.secondary.main,
        marginBottom: theme.spacing(2),
    },
    emptyState: {
        textAlign: 'center',
        padding: theme.spacing(6),
        color: '#64748b',
    },
    emptyIcon: {
        fontSize: '4rem',
        marginBottom: theme.spacing(2),
        opacity: 0.5,
    },
}));

function DeleteConfirmationModal({ open, onClose, onConfirm, containerCardId }) {
    const classes = useStyles();

    const handleConfirmClick = () => {
        onConfirm(containerCardId);
    };

    return (
        <Modal
            open={open}
            onClose={onClose}
            className={classes.modal}
            closeAfterTransition
            BackdropComponent={Backdrop}
            BackdropProps={{ timeout: 500 }}
        >
            <Fade in={open}>
                <div className={classes.modalContent}>
                    <div className={classes.deleteModalContent}>
                        <DeleteIcon className={classes.deleteIcon} />
                        <Typography variant="h6" gutterBottom>
                            Confirmer la suppression
                        </Typography>
                        <Typography variant="body2" style={{ marginBottom: 24, color: '#64748b' }}>
                            Êtes-vous sûr de vouloir supprimer ce contenant ? Cette action est irréversible.
                        </Typography>
                        <div className={classes.modalActions}>
                            <Button 
                                onClick={onClose} 
                                className={`${classes.modalButton} ${classes.cancelButton}`}
                            >
                                Annuler
                            </Button>
                            <Button 
                                onClick={handleConfirmClick} 
                                className={`${classes.modalButton}`}
                                style={{ 
                                    backgroundColor: '#dc2626', 
                                    color: 'white',
                                    '&:hover': { backgroundColor: '#b91c1c' }
                                }}
                            >
                                Supprimer
                            </Button>
                        </div>
                    </div>
                </div>
            </Fade>
        </Modal>
    );
}

function ManageContainerCardsPage() {
    const classes = useStyles();
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [containerCardToDelete, setContainerCardToDelete] = useState(null);
    const [containerCards, setContainerCards] = useState([]);
    const [addModalOpen, setAddModalOpen] = useState(false);
    const [updateModalOpen, setUpdateModalOpen] = useState(false);
    const [selectedContainerCard, setSelectedContainerCard] = useState(null);

    const fetchContainerCards = () => {
        axiosInstance.get('/containers')
            .then(response => {
                setContainerCards(response.data);
                console.log('container', response.data);
            })
            .catch(error => {
                console.error('Error fetching data: ', error);
            });
    };

    useEffect(fetchContainerCards, []);

    const handleAddClick = () => {
        setAddModalOpen(true);
    };

    const handleUpdateClick = (containerCard) => {
        setSelectedContainerCard(containerCard);
        setUpdateModalOpen(true);
    };

    const handleAddContainerCard = (containerCard) => {
        const formData = new FormData();
        formData.append('brand', containerCard.brand);
        formData.append('name', containerCard.name);
        formData.append('volume', containerCard.volume);
        formData.append('material', containerCard.material);
        formData.append('type', containerCard.type);
        formData.append('transparent', containerCard.isTransparent);
        
        if (containerCard.image instanceof File) {
            formData.append('file', containerCard.image);
        }

        axios({
            method: 'post',
            url: 'https://backend.csibon.app/containers',
            data: formData,
            headers: { 'Content-Type': 'multipart/form-data' }
        })
            .then(response => {
                fetchContainerCards();
                setAddModalOpen(false);
            })
            .catch(error => {
                console.error('Error adding data: ', error);
            });
    };

    const handleUpdateContainerCard = (updatedContainerCard) => {
        const formData = new FormData();
        formData.append('brand', updatedContainerCard.brand);
        formData.append('name', updatedContainerCard.name);
        formData.append('volume', updatedContainerCard.volume);
        formData.append('material', updatedContainerCard.material);
        formData.append('type', updatedContainerCard.type);
        formData.append('transparent', updatedContainerCard.isTransparent);
        
        if (updatedContainerCard.image) {
            formData.append('file', updatedContainerCard.image);
        }

        axiosInstance.put(`/containers/${updatedContainerCard.id}`, formData)
            .then(response => {
                fetchContainerCards();
                setUpdateModalOpen(false);
            })
            .catch(error => {
                console.error('Error updating data: ', error);
            });
    };

    const handleDeleteClick = (containerCardId) => {
        setContainerCardToDelete(containerCardId);
        setDeleteModalOpen(true);
    };

    const handleDeleteConfirmClick = (containerCardId) => {
        axiosInstance.delete(`/containers/${containerCardId}`)
            .then(response => {
                fetchContainerCards();
                setDeleteModalOpen(false);
            })
            .catch(error => {
                console.error('Error deleting data: ', error);
            });
    };

    const getContainerTypeIcon = (containerCard) => {
        if (containerCard.gourde) return '🚰';
        if (containerCard.mug) return '☕';
        if (containerCard.box) return '📦';
        return '📦';
    };

    const getContainerTypeName = (containerCard) => {
        if (containerCard.gourde) return 'Gourde';
        if (containerCard.mug) return 'Mug';
        if (containerCard.box) return 'Box';
        return 'N/A';
    };

    return (
        <ThemeProvider theme={theme}>
            <div className={classes.root}>
                <Navbar />
                
                {/* Header */}
                <div className={classes.header}>
                    <Container>
                        <div className={classes.headerContent}>
                            <div className={classes.headerTitle}>
                                <div>
                                    <Typography variant="h4">
                                        Gestion des Contenants
                                    </Typography>
                                    <Typography variant="body1" style={{ color: 'rgba(255, 255, 255, 0.9)', fontSize: '1.1rem' }}>
                                        Gérez votre catalogue de contenants réutilisables
                                    </Typography>
                                </div>
                            </div>
                            <div className={classes.headerIcon}>
                                <StorageIcon style={{ fontSize: '3rem' }} />
                                <EcoIcon style={{ fontSize: '2rem' }} />
                            </div>
                        </div>
                    </Container>
                </div>

                <Container>
                    {/* Bouton d'ajout */}
                    <div className={classes.addButtonContainer}>
                        <Button
                            variant="contained"
                            className={classes.addButton}
                            startIcon={<AddIcon />}
                            onClick={handleAddClick}
                        >
                            Ajouter un nouveau contenant
                        </Button>
                    </div>

                    {/* Grille des contenants */}
                    {containerCards.length === 0 ? (
                        <div className={classes.emptyState}>
                            <div className={classes.emptyIcon}>📦</div>
                            <Typography variant="h6" gutterBottom>
                                Aucun contenant enregistré
                            </Typography>
                            <Typography variant="body2">
                                Commencez par ajouter vos premiers contenants réutilisables.
                            </Typography>
                        </div>
                    ) : (
                        <Grid container spacing={3}>
                            {containerCards.map((containerCard) => (
                                <Grid item xs={12} sm={6} md={4} lg={3} key={containerCard.id}>
                                    <Card className={classes.card}>
                                        <CardContent className={classes.cardContent}>
                                            <img
                                                src={"https://backend.csibon.app/static/container_images/" + containerCard.image_url}
                                                alt={containerCard.brand}
                                                className={classes.cardImage}
                                            />
                                            <div className={classes.cardInfo}>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
                                                    <span style={{ fontSize: '1.2rem' }}>
                                                        {getContainerTypeIcon(containerCard)}
                                                    </span>
                                                    <Typography className={classes.cardName}>
                                                        {containerCard.name}
                                                    </Typography>
                                                </div>
                                                
                                                <div className={classes.cardDetail}>
                                                    <span className={classes.cardLabel}>Marque</span>
                                                    <span className={classes.cardValue}>{containerCard.brand}</span>
                                                </div>
                                                
                                                <div className={classes.cardDetail}>
                                                    <span className={classes.cardLabel}>Volume</span>
                                                    <span className={classes.cardValue}>{containerCard.volume}</span>
                                                </div>
                                                
                                                <div className={classes.cardDetail}>
                                                    <span className={classes.cardLabel}>Matériau</span>
                                                    <span className={classes.cardValue}>{containerCard.material}</span>
                                                </div>
                                                
                                                <div className={classes.cardDetail}>
                                                    <span className={classes.cardLabel}>Type</span>
                                                    <Chip
                                                        label={getContainerTypeName(containerCard)}
                                                        size="small"
                                                        className={classes.typeChip}
                                                    />
                                                </div>
                                                
                                                <Chip
                                                    icon={containerCard.transparent ? <CheckCircleIcon /> : <CancelIcon />}
                                                    label={containerCard.transparent ? 'Transparent' : 'Opaque'}
                                                    size="small"
                                                    className={`${classes.transparentChip} ${containerCard.transparent ? classes.transparentTrue : classes.transparentFalse}`}
                                                />
                                            </div>
                                            
                                            <div className={classes.actionButtons}>
                                                <Button
                                                    variant="contained"
                                                    className={classes.editButton}
                                                    startIcon={<EditIcon />}
                                                    onClick={() => handleUpdateClick(containerCard)}
                                                >
                                                    Modifier
                                                </Button>
                                                <Button
                                                    variant="contained"
                                                    className={classes.deleteButton}
                                                    startIcon={<DeleteIcon />}
                                                    onClick={() => handleDeleteClick(containerCard.id)}
                                                >
                                                    Supprimer
                                                </Button>
                                            </div>
                                        </CardContent>
                                    </Card>
                                </Grid>
                            ))}
                        </Grid>
                    )}
                </Container>

                <AddContainerCardModal 
                    open={addModalOpen} 
                    onClose={() => setAddModalOpen(false)} 
                    onAdd={handleAddContainerCard} 
                />
                <UpdateContainerCardModal 
                    open={updateModalOpen} 
                    onClose={() => setUpdateModalOpen(false)} 
                    onUpdate={handleUpdateContainerCard} 
                    containerCard={selectedContainerCard} 
                />
                <DeleteConfirmationModal 
                    open={deleteModalOpen} 
                    onClose={() => setDeleteModalOpen(false)} 
                    onConfirm={handleDeleteConfirmClick} 
                    containerCardId={containerCardToDelete} 
                />
            </div>
        </ThemeProvider>
    );
}

function UpdateContainerCardModal({ open, onClose, onUpdate, containerCard }) {
    const classes = useStyles();
    const [brand, setBrand] = useState(containerCard ? containerCard.brand : '');
    const [name, setName] = useState(containerCard ? containerCard.name : '');
    const [volume, setVolume] = useState(containerCard ? containerCard.volume : '');
    const [material, setMaterial] = useState(containerCard ? containerCard.material : '');
    const [image, setImage] = useState(null);
    const [type, setType] = useState('');
    const [isTransparent, setIsTransparent] = useState(containerCard ? containerCard.transparent : false);

    useEffect(() => {
        if (containerCard) {
            setBrand(containerCard.brand);
            setName(containerCard.name);
            setVolume(containerCard.volume);
            setMaterial(containerCard.material);
            setImage(null);
            setIsTransparent(containerCard.transparent);
            if (containerCard.gourde) {
                setType('gourde');
            } else if (containerCard.mug) {
                setType('mug');
            } else if (containerCard.box) {
                setType('box');
            }
        }
    }, [containerCard]);

    if (!containerCard) {
        return null;
    }

    const handleVolumeChange = (e) => {
        const { value } = e.target;
        if (/^\d*$/.test(value)) {
            setVolume(value);
        }
    };

    const handleUpdateClick = () => {
        let updatedContainerCard = {
            ...containerCard,
            name,
            brand,
            volume,
            material,
            image,
            type,
            isTransparent
        };
        if (image) {
            updatedContainerCard.image = image;
        }
        onUpdate(updatedContainerCard);
    };

    return (
        <Modal
            open={open}
            onClose={onClose}
            className={classes.modal}
            closeAfterTransition
            BackdropComponent={Backdrop}
            BackdropProps={{ timeout: 500 }}
        >
            <Fade in={open}>
                <div className={classes.modalContent}>
                    <div className={classes.modalHeader}>
                        <Typography className={classes.modalTitle}>
                            Modifier le contenant
                        </Typography>
                        <IconButton onClick={onClose} className={classes.closeButton}>
                            <CloseIcon />
                        </IconButton>
                    </div>
                    
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <TextField 
                                type="file" 
                                label="Image" 
                                onChange={e => setImage(e.target.files[0])} 
                                fullWidth 
                                variant="outlined"
                                className={classes.textField}
                                InputLabelProps={{ shrink: true }}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField 
                                label="Nom" 
                                value={name} 
                                onChange={e => setName(e.target.value)} 
                                fullWidth 
                                variant="outlined"
                                className={classes.textField}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField 
                                label="Marque" 
                                value={brand} 
                                onChange={e => setBrand(e.target.value)} 
                                fullWidth 
                                variant="outlined"
                                className={classes.textField}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField 
                                label="Volume" 
                                value={volume} 
                                onChange={handleVolumeChange} 
                                fullWidth 
                                variant="outlined"
                                className={classes.textField}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField 
                                label="Matériau" 
                                value={material} 
                                onChange={e => setMaterial(e.target.value)} 
                                fullWidth 
                                variant="outlined"
                                className={classes.textField}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <FormControl fullWidth variant="outlined" className={classes.textField}>
                                <InputLabel id="container-type-label">Type de contenant</InputLabel>
                                <Select
                                    labelId="container-type-label"
                                    value={type}
                                    onChange={e => setType(e.target.value)}
                                    label="Type de contenant"
                                >
                                    <MenuItem value="gourde">🚰 Gourde</MenuItem>
                                    <MenuItem value="mug">☕ Mug</MenuItem>
                                    <MenuItem value="box">📦 Box</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12}>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={isTransparent}
                                        onChange={e => setIsTransparent(e.target.checked)}
                                        name="isTransparent"
                                        color="primary"
                                    />
                                }
                                label="Contenant transparent"
                            />
                        </Grid>
                    </Grid>
                    
                    <div className={classes.modalActions}>
                        <Button 
                            onClick={onClose} 
                            className={`${classes.modalButton} ${classes.cancelButton}`}
                        >
                            Annuler
                        </Button>
                        <Button 
                            onClick={handleUpdateClick} 
                            className={`${classes.modalButton} ${classes.saveButton}`}
                        >
                            Mettre à jour
                        </Button>
                    </div>
                </div>
            </Fade>
        </Modal>
    );
}

function AddContainerCardModal({ open, onClose, onAdd }) {
    const classes = useStyles();
    const [brand, setBrand] = useState('');
    const [name, setName] = useState('');
    const [volume, setVolume] = useState('');
    const [material, setMaterial] = useState('');
    const [image, setImage] = useState(null);
    const [type, setType] = useState('');
    const [isTransparent, setIsTransparent] = useState(false);

    const handleVolumeChange = (e) => {
        const { value } = e.target;
        if (/^\d*$/.test(value)) {
            setVolume(value);
        }
    };

    const handleSaveClick = () => {
        const containerCard = {
            id: Math.random(),
            name,
            brand,
            volume,
            material,
            image,
            type,
            isTransparent
        };
        onAdd(containerCard);
        
        // Reset form
        setBrand('');
        setName('');
        setVolume('');
        setMaterial('');
        setImage(null);
        setType('');
        setIsTransparent(false);
    };

    return (
        <Modal
            open={open}
            onClose={onClose}
            className={classes.modal}
            closeAfterTransition
            BackdropComponent={Backdrop}
            BackdropProps={{ timeout: 500 }}
        >
            <Fade in={open}>
                <div className={classes.modalContent}>
                    <div className={classes.modalHeader}>
                        <Typography className={classes.modalTitle}>
                            Ajouter un nouveau contenant
                        </Typography>
                        <IconButton onClick={onClose} className={classes.closeButton}>
                            <CloseIcon />
                        </IconButton>
                    </div>
                    
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <TextField 
                                type="file" 
                                label="Image" 
                                onChange={e => setImage(e.target.files[0])} 
                                fullWidth 
                                variant="outlined"
                                className={classes.textField}
                               InputLabelProps={{ shrink: true }}
                           />
                       </Grid>
                       <Grid item xs={12} sm={6}>
                           <TextField 
                               label="Nom" 
                               value={name} 
                               onChange={e => setName(e.target.value)} 
                               fullWidth 
                               variant="outlined"
                               className={classes.textField}
                               required
                           />
                       </Grid>
                       <Grid item xs={12} sm={6}>
                           <TextField 
                               label="Marque" 
                               value={brand} 
                               onChange={e => setBrand(e.target.value)} 
                               fullWidth 
                               variant="outlined"
                               className={classes.textField}
                               required
                           />
                       </Grid>
                       <Grid item xs={12} sm={6}>
                           <TextField 
                               label="Volume" 
                               value={volume} 
                               onChange={handleVolumeChange} 
                               fullWidth 
                               variant="outlined"
                               className={classes.textField}
                               placeholder="ex: 500ml"
                               required
                           />
                       </Grid>
                       <Grid item xs={12} sm={6}>
                           <TextField 
                               label="Matériau" 
                               value={material} 
                               onChange={e => setMaterial(e.target.value)} 
                               fullWidth 
                               variant="outlined"
                               className={classes.textField}
                               placeholder="ex: Plastique, Métal, Verre"
                               required
                           />
                       </Grid>
                       <Grid item xs={12}>
                           <FormControl fullWidth variant="outlined" className={classes.textField} required>
                               <InputLabel id="container-type-label">Type de contenant</InputLabel>
                               <Select
                                   labelId="container-type-label"
                                   value={type}
                                   onChange={e => setType(e.target.value)}
                                   label="Type de contenant"
                               >
                                   <MenuItem value="gourde">🚰 Gourde</MenuItem>
                                   <MenuItem value="mug">☕ Mug</MenuItem>
                                   <MenuItem value="box">📦 Box</MenuItem>
                               </Select>
                           </FormControl>
                       </Grid>
                       <Grid item xs={12}>
                           <FormControlLabel
                               control={
                                   <Checkbox
                                       checked={isTransparent}
                                       onChange={e => setIsTransparent(e.target.checked)}
                                       name="isTransparent"
                                       color="primary"
                                   />
                               }
                               label="Contenant transparent"
                           />
                       </Grid>
                   </Grid>
                   
                   <div className={classes.modalActions}>
                       <Button 
                           onClick={onClose} 
                           className={`${classes.modalButton} ${classes.cancelButton}`}
                       >
                           Annuler
                       </Button>
                       <Button 
                           onClick={handleSaveClick} 
                           className={`${classes.modalButton} ${classes.saveButton}`}
                           disabled={!name || !brand || !volume || !material || !type}
                       >
                           Sauvegarder
                       </Button>
                   </div>
               </div>
           </Fade>
       </Modal>
   );
}

export default ManageContainerCardsPage;